// Base URLs for all backend microservices
export const SERVICES = {
  BOOKING: process.env.NEXT_PUBLIC_BOOKING_API_URL || '',
  CUSTOMER: process.env.NEXT_PUBLIC_ADMIN_CUSTOMER_PROFILES_API_URL || '',
  ADMIN: process.env.NEXT_PUBLIC_ADMIN_HOTEL_SERVICES_API_URL || '',
  MARKUP: process.env.NEXT_PUBLIC_MARKUP_MANAGEMENT_API_URL || '',
  AUTH: process.env.NEXT_PUBLIC_AUTH_API_URL || '',
  CONTENT: process.env.CONTENT_SERVICE_API_URL || '',
  CDN: process.env.NEXT_PUBLIC_CDN_BASE_URL || '',
};

const MEDIA_BASE = `${SERVICES.CONTENT}/content-service/api/v1/admin/media-management`;
const mediaHotel = (hotelId: string) => `${MEDIA_BASE}/hotels/${encodeURIComponent(hotelId)}`;

// Centralized endpoint mappings
export const API_ENDPOINTS = {
  BOOKINGS: {
    LIST: (query?: string) => `${SERVICES.BOOKING}/booking-service/api/v1/admin/bookings${query ? `?${query}` : ''}`,
    SUMMARY: (query?: string) => `${SERVICES.BOOKING}/booking-service/api/v1/admin/bookings/summary${query ? `?${query}` : ''}`,
    EXPORTS: () => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/bookings/exports`,
    DETAILS: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/admin/bookings/${id}`,
  },
  HOTEL_AMENDMENTS: {
    LIST: (query?: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-amendments${query ? `?${query}` : ''}`,
    DETAILS: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-amendments/${id}`,
    COMPLETE: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-amendments/${id}/complete`,
    RELEASE: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-amendments/${id}/release`,
    REVIEWED_CHANGES: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-amendments/${id}/reviewed-changes`,
    START: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-amendments/${id}/start`,
    REJECT: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-amendments/${id}/reject`,
    PROCESS: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-amendments/${id}/process`,
  },
  HOTEL_CANCELLATIONS: {
    LIST: (query?: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-cancellations${query ? `?${query}` : ''}`,
    DETAILS: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-cancellations/${id}`,
    VERIFY_ACCESS: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-cancellations/${id}/verify-access`,
    RELEASE: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-cancellations/${id}/release`,
    START: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-cancellations/${id}/start`,
    COMPLETE: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/hotel-cancellations/${id}/complete`,
  },
  BOOKING_QUEUE: {
    LIST: (query?: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/booking-queue${query ? `?${query}` : ''}`,
  },
  CUSTOMERS: {
    LIST: (query?: string) => `${SERVICES.CUSTOMER}/admin/customers${query ? `?${query}` : ''}`,
    CREATE: () => `${SERVICES.CUSTOMER}/admin/customers`,
    SUMMARY: (query?: string) => `${SERVICES.CUSTOMER}/admin/customers/summary${query ? `?${query}` : ''}`,
    DETAILS: (ref: string) => `${SERVICES.CUSTOMER}/admin/customers/${ref}`,
    UPDATE: (ref: string) => `${SERVICES.CUSTOMER}/admin/customers/${ref}`,
    DELETE: (ref: string) => `${SERVICES.CUSTOMER}/admin/customers/${ref}`,
    REVEAL: (ref: string) => `${SERVICES.CUSTOMER}/admin/customers/${ref}/reveal`,
  },
  PROGRAMS: {
    LIST: (query?: string) => `${SERVICES.ADMIN}/api/v1/admin/gds-rate-programs${query ? `?${query}` : ''}`,
    METADATA: () => `${SERVICES.ADMIN}/api/v1/admin/gds-rate-programs/metadata`,
    CREATE: () => `${SERVICES.ADMIN}/api/v1/admin/gds-rate-programs`,
    DETAILS: (id: string) => `${SERVICES.ADMIN}/api/v1/admin/gds-rate-programs/${id}`,
    STATUS: (id: string) => `${SERVICES.ADMIN}/api/v1/admin/gds-rate-programs/${id}/status`,
  },
  MARKUP_CONFIGS: {
    LIST: (query?: string) => `${SERVICES.ADMIN}/api/v1/admin/markup-configs${query ? `?${query}` : ''}`,
    DETAILS: (id: string) => `${SERVICES.ADMIN}/api/v1/admin/markup-configs/${id}`,
  },
  DASHBOARD: {
    OVERVIEW: (query?: string) => `${SERVICES.ADMIN}/api/v1/admin/dashboard/overview${query ? `?${query}` : ''}`,
  },
  RULE_MASTER: {
    SUMMARY: () => `${SERVICES.ADMIN}/api/v1/admin/rule-master/summary`,
    CATEGORIES: () => `${SERVICES.ADMIN}/api/v1/admin/rule-master/categories`,
    DISCOUNT_METADATA: () => `${SERVICES.ADMIN}/api/v1/admin/rule-master/discount-rules/metadata`,
    CREATE_DISCOUNT_RULE: () => `${SERVICES.ADMIN}/api/v1/admin/rule-master/discount-rules`,
    LIST_DISCOUNT_RULES: (query?: string) => `${SERVICES.ADMIN}/api/v1/admin/rule-master/discount-rules${query ? `?${query}` : ''}`,
    DETAILS: (id: string) => `${SERVICES.ADMIN}/api/v1/admin/rule-master/discount-rules/${id}`,
    ACTIVATE: (id: string) => `${SERVICES.ADMIN}/api/v1/admin/rule-master/discount-rules/${id}/activate`,
    DEACTIVATE: (id: string) => `${SERVICES.ADMIN}/api/v1/admin/rule-master/discount-rules/${id}/deactivate`,
    CHAINS_AUTOCOMPLETE: (query?: string) => `${SERVICES.ADMIN}/api/v1/admin/rule-master/chains/autocomplete${query ? `?${query}` : ''}`,
    HOTELS_SEARCH: (supplierId: string, query?: string) => `${SERVICES.ADMIN}/api/v1/admin/rule-master/suppliers/${supplierId}/hotels/search${query ? `?${query}` : ''}`,

    // Comparison Rules Endpoints
    COMPARISON_METADATA: () => `${SERVICES.ADMIN}/api/v1/admin/rule-master/comparison-rules/metadata`,
    LIST_COMPARISON_RULES: (query?: string) => {
      const q = query?.replace(/^\?+/, '');
      return `${SERVICES.ADMIN}/api/v1/admin/rule-master/comparison-rules${q ? `?${q}` : ''}`;
    },
    CREATE_COMPARISON_RULE: () => `${SERVICES.ADMIN}/api/v1/admin/rule-master/comparison-rules`,
    DELETE_COMPARISON_RULE: (id: string) =>
      `${SERVICES.ADMIN}/api/v1/admin/rule-master/comparison-rules/${id}`,
    ACTIVATE_COMPARISON_RULE: (id: string) =>
      `${SERVICES.ADMIN}/api/v1/admin/rule-master/comparison-rules/${id}/activate`,
    DEACTIVATE_COMPARISON_RULE: (id: string) =>
      `${SERVICES.ADMIN}/api/v1/admin/rule-master/comparison-rules/${id}/deactivate`,
    GET_COMPARISON_RULE_DETAILS: (id: string) =>
      `${SERVICES.ADMIN}/api/v1/admin/rule-master/comparison-rules/${id}`,
    UPDATE_COMPARISON_RULE: (id: string) =>
      `${SERVICES.ADMIN}/api/v1/admin/rule-master/comparison-rules/${id}`,
  },
  CMS: {
    PAGES: () => `${SERVICES.ADMIN}/api/v1/admin/pages`,
    SECTIONS: (pageKey: string) => `${SERVICES.ADMIN}/api/v1/admin/pages/${pageKey}/sections`,
    SECTION_ITEMS: (pageId: string, sectionId: string) => `${SERVICES.ADMIN}/api/v1/admin/pages/${pageId}/sections/${sectionId}/items`,
    SECTION_ITEM_DETAILS: (pageId: string, sectionId: string, itemId: string) => `${SERVICES.ADMIN}/api/v1/admin/pages/${pageId}/sections/${sectionId}/items/${itemId}`,
    HOTELS: () => `${SERVICES.ADMIN}/api/v1/admin/hotels`,
    IMAGE: (imagePath: string) => {
      if (!imagePath) return '';
      if (imagePath.startsWith('http') || imagePath.startsWith('blob:')) return imagePath;
      return `${SERVICES.ADMIN}/${imagePath.replace(/^\/+/, '')}`;
    }
  },
  MEDIA_MANAGEMENT: {
    HOTELS_SEARCH: (query?: string) => `${MEDIA_BASE}/hotels/search${query ? `?${query}` : ''}`,
    CATEGORIES: () => `${MEDIA_BASE}/categories`,
    CONFIGURATION: () => `${MEDIA_BASE}/configuration`,
    HOTEL_SUMMARY: (hotelId: string) => `${mediaHotel(hotelId)}/summary`,
    HOTEL_IMAGES: (hotelId: string) => `${mediaHotel(hotelId)}/images`,
    IMAGE_DETAILS: (hotelId: string, imageId: string) => `${mediaHotel(hotelId)}/images/${encodeURIComponent(imageId)}`,
    PROCESSING_STATUS: (hotelId: string) => `${mediaHotel(hotelId)}/processing-status`,
    UPLOAD_INITIATE: (hotelId: string) => `${mediaHotel(hotelId)}/images/upload-initiate`,
    UPLOAD_COMPLETE: (hotelId: string) => `${mediaHotel(hotelId)}/images/upload-complete`,
    REMOVE_DRAFT: (hotelId: string, imageId: string, query?: string) =>
      `${mediaHotel(hotelId)}/images/${encodeURIComponent(imageId)}/draft${query ? `?${query}` : ''}`,
    SUBMIT: (hotelId: string) => `${mediaHotel(hotelId)}/submit`,
    RETRY: (hotelId: string, imageId: string) => `${mediaHotel(hotelId)}/images/${encodeURIComponent(imageId)}/retry`,
    HOTEL_ROOMS: (hotelId: string) => `${mediaHotel(hotelId)}/rooms`,
  },
  NAVIGATION: {
    MENUS: () => `${SERVICES.ADMIN}/api/v1/admin/me/navigation`,
  },
  JET_REQUESTS: {
    LIST: (query?: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/requests?vertical_type=JET${query ? `&${query}` : ''}`,
    SUMMARY: (query?: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/requests/metrics?vertical_type=JET${query ? `&${query}` : ''}`,
  },
  YACHT_REQUESTS: {
    LIST: (query?: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/requests?vertical_type=YACHT${query ? `&${query}` : ''}`,
    SUMMARY: (query?: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/requests/metrics?vertical_type=YACHT${query ? `&${query}` : ''}`,
  },
  REQUESTS: {
    DETAILS: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/requests/${id}`,
    STATUS: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/requests/${id}/status`,
    COMPLETE: (id: string) => `${SERVICES.BOOKING}/booking-service/api/v1/backoffice/requests/${id}/complete`,
  }
};
